import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Clapperboard, Loader2, Mail, Lock, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    const msg = params.get("msg");
    if (msg) setError(msg);
  }, [params]);

  useEffect(() => {
    api.get("/settings/public")
      .then(({ data }) => setGoogleEnabled(!!data.google_login_enabled))
      .catch(() => setGoogleEnabled(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message || "Request sent to your admin.");
      setMode("login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen bg-[#050505]">
      <div className="relative hidden w-1/2 overflow-hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1709377195538-5522ed0f9e10?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="studio"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        <div className="absolute bottom-14 left-14 right-14">
          <div className="mb-4 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#00E5FF] text-black">
              <Clapperboard className="h-5 w-5" />
            </div>
            <span className="font-heading text-2xl font-bold">ReelForge</span>
          </div>
          <h2 className="font-heading text-4xl font-bold leading-tight">
            Brand your reels.<br />Publish everywhere.
          </h2>
          <p className="mt-4 max-w-md text-base text-gray-400">
            Private team workspace. Access is invite-only — an admin approves every account.
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm rf-fade-up">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#00E5FF] text-black">
              <Clapperboard className="h-5 w-5" />
            </div>
            <span className="font-heading text-xl font-bold">ReelForge</span>
          </div>

          {mode === "login" ? (
            <>
              <h1 className="font-heading text-3xl font-bold">Welcome back</h1>
              <p className="mt-2 text-sm text-gray-400">Sign in to your team workspace</p>

              {googleEnabled && (
                <>
                  <button
                    onClick={googleLogin}
                    data-testid="google-login-btn"
                    className="mt-8 flex h-11 w-full items-center justify-center gap-3 rounded-md border border-white/15 bg-[#1A1A1A] font-medium text-white transition-colors hover:bg-white/5"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.66 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1S8.7 6 12 6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.5 14.6 2.5 12 2.5 6.9 2.5 2.8 6.6 2.8 12S6.9 21.5 12 21.5c6 0 9.1-4.2 9.1-9.1 0-.6-.06-1-.14-1.5H12z" />
                    </svg>
                    Continue with Google
                  </button>

                  <div className="my-5 flex items-center gap-3">
                    <div className="h-px flex-1 bg-white/10" />
                    <span className="text-xs uppercase tracking-widest text-gray-600">or use password</span>
                    <div className="h-px flex-1 bg-white/10" />
                  </div>
                </>
              )}

              <form onSubmit={submit} className={`space-y-4 ${googleEnabled ? "" : "mt-8"}`} data-testid="auth-form">
                <Field icon={Mail} value={email} onChange={setEmail} placeholder="you@company.com" testId="email-input" type="email" />
                <Field icon={Lock} value={password} onChange={setPassword} placeholder="Password" testId="password-input" type="password" />

                {error && (
                  <div data-testid="auth-error" className="rounded-md border border-[#FF3B30]/30 bg-[#FF3B30]/10 px-3 py-2 text-sm text-[#FF3B30]">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="auth-submit"
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#00E5FF] font-semibold text-black transition-colors hover:bg-[#33EBFF] disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Sign in
                </button>
              </form>

              <button
                onClick={() => { setMode("forgot"); setError(""); }}
                data-testid="forgot-link"
                className="mt-6 text-sm text-gray-400 transition-colors hover:text-[#00E5FF]"
              >
                Forgot your password?
              </button>
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-bold">Reset password</h1>
              <p className="mt-2 text-sm text-gray-400">
                Enter your account email. Your admin will be notified to set a new password for you.
              </p>
              <form onSubmit={submitForgot} className="mt-8 space-y-4" data-testid="forgot-form">
                <Field icon={Mail} value={email} onChange={setEmail} placeholder="you@company.com" testId="forgot-email-input" type="email" />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-submit"
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#00E5FF] font-semibold text-black transition-colors hover:bg-[#33EBFF] disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send reset request
                </button>
              </form>
              <button
                onClick={() => { setMode("login"); setError(""); }}
                data-testid="back-to-login"
                className="mt-6 flex items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-[#00E5FF]"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, value, onChange, placeholder, testId, type }) {
  return (
    <div className="relative">
      <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
      <input
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="h-11 w-full rounded-md border border-white/10 bg-[#1A1A1A] pl-10 pr-3 text-sm text-white placeholder:text-gray-600 focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
      />
    </div>
  );
}
